import { useState, useEffect, useCallback } from "react";
import { api, ApiError } from "@/lib/api-client";

/**
 * Phase 18 WU-18.F4 — hook for `/api/analytics/batches/:id/trail`.
 *
 * Same shape/contract as useDocumentTrail. 410 Gone is surfaced as
 * `error.status === 410` so pages can render a guard panel instead of a
 * generic error.
 */
export interface BatchTrailResponse {
  batch: {
    id: string;
    name: string;
    /** WU-7 — structured displayId, null for legacy rows. */
    displayId: string | null;
    /** R-167 WU-7 — operator-rendered frozen label, null without a template. */
    customDisplayLabel: string | null;
  };
  batchStats: {
    id: string;
    tenantId: string;
    batchId: string;
    jobTypeId: string;
    createdAt: string;
    completedAt: string;
    totalDurationMs: number;
    stepDurations: unknown;
    docCount: number;
    docCountBySlaStatus: unknown;
    operatorsInvolved: unknown;
    slaTargetMs: number | null;
    slaMet: boolean | null;
    slaBreachedAt: string | null;
  } | null;
  timeline: Array<{
    at: string;
    action: string;
    by: string;
    metadata: unknown;
  }>;
  docs: Array<{
    documentId: string;
    completedAt: string;
    totalDurationMs: number;
    primaryOperator: string | null;
    slaMet: boolean | null;
    correctionCount: number;
    keystrokeCount: number;
  }>;
}

export interface TrailError {
  status: number;
  message: string;
}

export function useBatchTrail(batchId: string) {
  const [data, setData] = useState<BatchTrailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<TrailError | null>(null);

  const fetchData = useCallback(async () => {
    if (!batchId) {
      setIsLoading(false);
      setError({ status: 400, message: "batchId required" });
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.get<BatchTrailResponse>(
        `/analytics/batches/${encodeURIComponent(batchId)}/trail`,
      );
      setData(result);
    } catch (e) {
      if (e instanceof ApiError) {
        setError({
          status: e.status,
          message:
            e.status === 410
              ? "Detail retained 90 days — summary available in monthly rollups."
              : e.status === 404
                ? "Batch not found."
                : e.status === 403
                  ? "You do not have permission to view this batch's trail."
                  : `Failed to load trail (${e.status}).`,
        });
      } else if (e instanceof Error) {
        setError({ status: 0, message: e.message });
      } else {
        setError({ status: 0, message: "Failed to load trail" });
      }
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
